import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, Paper, Switch, Typography,
} from '@mui/material';
import { can } from '../../utils/roleUtils';
import { PORTAL_FEATURES, defaultOn } from '../../services/portal/portalFeatures';
import { portalFeaturesService } from '../../services/portal/portalFeatures.service';

/**
 * Settings › Portal › Client portal features: what every client's portal gets unless the client is
 * set differently (Add/Edit Client › Portal access). 14-portal/PORTAL-EMPLOYEES-AND-FEATURES.md §4.1.
 */
const PortalFeatureSettings = ({ currentUser }) => {
  const canWrite = can(currentUser, 'settings', 'write');
  const thaklisDisabled = currentUser?.thaklisEnabled === false;
  const [defaults, setDefaults] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Success notes close on their own; warnings and errors stay until closed.
  useEffect(() => {
    if (message?.severity !== 'success') return undefined;
    const t = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    let active = true;
    portalFeaturesService.getDefaults()
      .then((d) => { if (active) { setDefaults(d || {}); setSaved(d || {}); } })
      .catch(() => { if (active) setMessage({ severity: 'error', text: 'Failed to load portal settings' }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const features = PORTAL_FEATURES.filter((f) => !(f.thaklisOnly && thaklisDisabled));
  const dirty = features.some((f) => defaultOn(defaults, f.key) !== defaultOn(saved, f.key));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Store only what is switched off; missing = on.
      const off = Object.fromEntries(features.filter((f) => !defaultOn(defaults, f.key)).map((f) => [f.key, false]));
      const result = await portalFeaturesService.saveDefaults(off);
      setDefaults(result);
      setSaved(result);
      setMessage({ severity: 'success', text: 'Saved. Clients on "Default" follow these settings.' });
    } catch (e) {
      setMessage({ severity: 'error', text: e?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, maxWidth: 720 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Client portal features</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
        What clients get in their portal unless you set it differently on the client (Organizations › Edit › Portal access).
        Dashboard, Quotations, Work Orders and Invoices are always on.
      </Typography>

      {message && <Alert severity={message.severity} sx={{ mb: 1.5 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      {features.map((f, i) => (
        <React.Fragment key={f.key}>
          {i > 0 && <Divider />}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>{f.label}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{f.hint}</Typography>
            </Box>
            <Switch
              size="small"
              checked={defaultOn(defaults, f.key)}
              disabled={!canWrite || saving}
              onChange={(e) => setDefaults((prev) => ({ ...prev, [f.key]: e.target.checked }))}
              inputProps={{ 'aria-label': f.label }}
            />
          </Box>
        </React.Fragment>
      ))}

      {canWrite && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
          <Button size="small" disabled={!dirty || saving} onClick={() => setDefaults(saved)} sx={{ textTransform: 'none' }}>
            Reset
          </Button>
          <Button size="small" variant="contained" disabled={!dirty || saving} onClick={save} sx={{ textTransform: 'none' }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PortalFeatureSettings;
